import React, { useEffect, useState } from 'react'
import { Box, Chip, TextField, Button, Typography, IconButton } from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import BlockIcon from '@mui/icons-material/Block'
import VisibilityIcon from '@mui/icons-material/Visibility'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { StepProps } from '../../../components/_WMS/Stepper'
import { AsnReceptionState, AsnReceptionLpnInfoDto, eStepAsnReception } from '../types'
import { getAsnReceptionLpnInfo, receiveAsnLpns } from '../api'
import { Loading } from '../../../components/UI'
import { Modal } from '../../../components/_EOS/Modal'
import { ScanIcon } from '../../../components/_WMS/_icons/ScanIcon'
import { CameraScanButton } from '../../../components/_WMS/CameraScan'
import { useScanInput } from '../../../hooks/useScanInput'
import { formatExpirationDate } from '../../../utils/dateHelper'
import { ViewDetailStep } from './ViewDetailStep'

interface ReceptionStepProps extends StepProps<AsnReceptionState> {}

const fabStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer',
  border: '1px solid #E0E0E0', borderRadius: '12px', height: '40px',
  padding: '0 10px', fontSize: '14px', fontWeight: 500, color: '#666'
}

export const ReceptionStep: React.FC<ReceptionStepProps> = ({ sharedState, setSharedState, prev, goToStep }) => {
  const asnId = sharedState.selectedResult?.asnId ?? 0

  const [info, setInfo] = useState<AsnReceptionLpnInfoDto | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [scannedLpns, setScannedLpns] = useState<string[]>([])
  const [isFlipped, setIsFlipped] = useState(false)

  const [duplicateModal, setDuplicateModal] = useState(false)
  const [duplicateLpn, setDuplicateLpn] = useState('')
  const [unknownModal, setUnknownModal] = useState(false)
  const [unknownLpn, setUnknownLpn] = useState('')
  const [incompleteModal, setIncompleteModal] = useState(false)
  const [backConfirmModal, setBackConfirmModal] = useState(false)

  /**
   * Aca el input no busca: acumula. Igual sirve el mismo hook, porque el cableado
   * (Enter, tipeo, camara, refoco) es identico; lo que cambia es que onSearch
   * agrega a la lista en vez de disparar una consulta.
   */
  const scan = useScanInput({
    onSearch: (value) => addLpn(value),
    disabled: !info || saving,
    trimOnChange: false
  })

  useEffect(() => {
    setSharedState((prev) => ({ ...prev, hideBackButton: true }))
    return () => { setSharedState((prev) => ({ ...prev, hideBackButton: false })) }
  }, [])

  useEffect(() => {
    if (!asnId) return
    getAsnReceptionLpnInfo(asnId)
      .then(({ data }) => setInfo(data as AsnReceptionLpnInfoDto))
      .finally(() => setLoading(false))
  }, [asnId])

  /**
   * Acumula la LPN leida. Recibe el valor por parametro y no del estado, asi la
   * camara puede invocarla con el valor recien leido sin esperar el re-render.
   */
  const addLpn = (rawValue: string) => {
    const value = rawValue.trim()
    if (!value || !info) return
    scan.reset()
    if (!info.lpns.includes(value)) { setUnknownLpn(value); setUnknownModal(true); return }
    if (scannedLpns.includes(value)) { setDuplicateLpn(value); setDuplicateModal(true); return }
    setScannedLpns((prev) => [...prev, value])
  }

  const handleRemoveLpn = (lpn: string) => setScannedLpns((prev) => prev.filter((l) => l !== lpn))

  const handleBack = () => {
    if (scannedLpns.length > 0) { setBackConfirmModal(true); return }
    prev()
  }

  const missingLpns = info?.lpns.filter((lpn) => !scannedLpns.includes(lpn)) ?? []

  const handleFinalizar = () => {
    if (missingLpns.length > 0) { setIncompleteModal(true); return }
    doReceive()
  }

  const doReceive = () => {
    setIncompleteModal(false)
    setSaving(true)
    receiveAsnLpns(asnId, scannedLpns)
      .then(() => {
        setSharedState((prev) => ({ ...prev, successMessage: 'Recepción completada correctamente' }))
        goToStep(eStepAsnReception.ScanStep)
      })
      .catch(() => setSaving(false))
  }

  if (loading || saving) return <Loading />

  const totalLpns = info?.lpns.length ?? 0
  const controlled = scannedLpns.length
  const pending = totalLpns - controlled
  const isLong = (val: string) => val.length > 15
  const createdDate = info?.createdDate ? formatExpirationDate(info.createdDate, 'dd-MM-yyyy') : '—'
  const chipFields = [
    { label: 'ASN', value: info?.asnNumber ?? '—', fullRow: true },
    { label: 'Marca', value: info?.supplierOriginName ?? '—', fullRow: false },
    { label: 'Creada', value: createdDate, fullRow: false },
  ]

  if (isFlipped) return <ViewDetailStep info={info!} scannedLpns={scannedLpns} onClose={() => setIsFlipped(false)} />

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: '0 4px' }}>

      {/* HEADER */}
      <Box sx={{ width: '100%', borderRadius: '10px', border: '1px solid #e0e0e0', overflow: 'hidden', backgroundColor: 'background.paper' }}>
        <Box sx={{ borderBottom: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1, py: '6px', minHeight: '48px' }}>
          <Box onClick={handleBack} sx={fabStyle}><ArrowBackIcon style={{ fontSize: 18, color: '#1976d2' }} />Volver</Box>
          <Box onClick={() => setIsFlipped(true)} sx={fabStyle}><VisibilityIcon style={{ fontSize: 18, color: '#1976d2' }} />Ver detalle</Box>
        </Box>
        <Box sx={{ px: 1.5, py: '10px', display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '6px', borderBottom: '1px solid #e0e0e0' }}>
          {chipFields.map(({ label, value, fullRow }) => (
            <Box key={label} sx={{ gridColumn: fullRow || isLong(value) ? 'span 2' : 'span 1' }}>
              <Chip label={`${label}: ${value}`} color='primary' variant='outlined' style={{ fontSize: '0.8125rem', width: '100%' }} />
            </Box>
          ))}
        </Box>
        <Box sx={{ px: 1.5, py: '8px', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
          {[
            { label: 'Pendientes', value: pending, color: '#3B6D11' },
            { label: 'Controladas', value: controlled, color: '#185FA5' },
            { label: 'LPNs Totales', value: totalLpns, color: 'text.primary' }
          ].map(({ label, value, color }) => (
            <Box key={label} sx={{ backgroundColor: '#f5f5f5', borderRadius: '8px', p: '6px 8px', textAlign: 'center' }}>
              <Typography sx={{ fontSize: 10, color: 'text.secondary', lineHeight: 1.3 }}>{label}</Typography>
              <Typography sx={{ fontSize: 18, fontWeight: 500, color, lineHeight: 1.2 }}>{value}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* INPUT */}
      <Box sx={{ px: '3px' }}>
        <TextField fullWidth variant='outlined' size='small' placeholder='Escanea o escribe la LPN'
          {...scan.fieldProps}
          autoComplete='off'
          slotProps={{ input: { endAdornment: <ScanIcon width={28} height={36} /> } }}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
        />

        {/* El margen va en el propio boton y no en un Box contenedor: cuando el
            modo de captura es handheld el boton devuelve null, y un wrapper
            dejaria un hueco de 12px colgando debajo del input. */}
        <CameraScanButton
          {...scan.cameraProps}
          disabled={!info || saving}
          dialogTitle='Escanear LPN'
          helperText='Centra el código de la LPN dentro del recuadro'
          sx={{ mt: 1.5 }}
        />
      </Box>

      {/* BOTÓN FINALIZAR */}
      <Box sx={{ px: '3px' }}>
        <Button fullWidth variant='contained' onClick={handleFinalizar}
          disabled={scannedLpns.length === 0}
          style={{ backgroundColor: scannedLpns.length === 0 ? undefined : '#32CD32', borderRadius: '10px', textTransform: 'none', fontWeight: 700, fontSize: '15px', padding: '12px' }}>
          Finalizar recepción de LPN
        </Button>
      </Box>

      {/* LISTA LPNs ESCANEADAS */}
      {scannedLpns.length > 0 && (
        <Box sx={{ px: '3px', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography variant='body2' sx={{ fontWeight: 600, color: '#555', fontSize: '12px', letterSpacing: 0.5, textTransform: 'uppercase' }}>
            LPNs de esta ASN
          </Typography>
          {scannedLpns.map((lpn) => (
            <Box key={lpn} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', border: '1px solid #e0e0e0', borderRadius: '8px', px: 2, py: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckIcon sx={{ color: 'success.main', fontSize: 18 }} />
                <Typography variant='body2' fontWeight='bold'>{lpn}</Typography>
              </Box>
              <IconButton size='small' onClick={() => handleRemoveLpn(lpn)}><CloseIcon fontSize='small' /></IconButton>
            </Box>
          ))}
        </Box>
      )}

      {/* MODAL: LPN ya escaneada */}
      <Modal open={duplicateModal} title='LPN ya escaneada' maxWidth='xs' onClose={() => setDuplicateModal(false)}
        successButton={{ action: () => setDuplicateModal(false), title: 'Aceptar' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 56, height: 56, borderRadius: '50%', backgroundColor: '#fff8e1', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <WarningAmberIcon sx={{ fontSize: 32, color: '#f59e0b' }} />
          </Box>
          <Typography variant='body1' textAlign='center'>
            La LPN <b style={{ color: '#d97706' }}>{duplicateLpn}</b> ya fue escaneada anteriormente. Verifica que no sea un duplicado físico.
          </Typography>
        </Box>
      </Modal>

      {/* MODAL: LPN no reconocida */}
      <Modal open={unknownModal} title='LPN no reconocida' maxWidth='xs' onClose={() => setUnknownModal(false)}
        successButton={{ action: () => setUnknownModal(false), title: 'Aceptar' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 56, height: 56, borderRadius: '50%', backgroundColor: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <BlockIcon sx={{ fontSize: 32, color: '#dc2626' }} />
          </Box>
          <Typography variant='body1' textAlign='center'>
            La LPN <b style={{ color: '#dc2626' }}>{unknownLpn}</b> no pertenece a la ASN seleccionada. Verifica el código e intenta nuevamente.
          </Typography>
        </Box>
      </Modal>

      {/* MODAL: Recepción incompleta */}
      <Modal open={incompleteModal} title='' maxWidth='xs' onClose={() => setIncompleteModal(false)}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 38, height: 38, borderRadius: '50%', backgroundColor: '#fff8e1', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <WarningAmberIcon sx={{ fontSize: 23, color: '#f59e0b' }} />
            </Box>
            <Typography variant='subtitle1' fontWeight='bold' textAlign='center'>Recepción incompleta</Typography>
            <Typography variant='body2' color='text.secondary' textAlign='center'>
              Controladas <b>{controlled}</b> de <b>{totalLpns}</b> LPNs
            </Typography>
          </Box>

          {/* LPNs faltantes */}
          <Box>
            <Typography variant='overline' sx={{ fontWeight: 600, color: 'error.main', letterSpacing: 1 }}>
              No Recibidas
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.2, maxHeight: 140, overflowY: 'auto', border: '1px solid #fecaca', borderRadius: '8px', p: 1 }}>
              {missingLpns.map((lpn) => (
                <Box key={lpn} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', border: '1px solid #fca5a5', borderRadius: '8px', px: 2, py: 1, backgroundColor: '#fff5f5' }}>
                  <Typography variant='body2' fontWeight='bold' sx={{ color: 'error.main' }}>{lpn}</Typography>
                  <Chip label='No recibida' size='small' sx={{ backgroundColor: '#fee2e2', color: '#dc2626', fontWeight: 600, borderRadius: '20px', fontSize: '0.72rem' }} />
                </Box>
              ))}
            </Box>
          </Box>

          {/* Advertencia */}
          <Box sx={{ backgroundColor: '#fffde7', border: '1px solid #ffe082', borderRadius: '8px', p: 0.5 }}>
            <Typography variant='body2' sx={{ color: '#92610a' }}>
              Al confirmar, las LPNs no encontradas <b>no podrán ingresarse</b> y sus unidades se registrarán automáticamente como <b>diferencia</b>.
            </Typography>
          </Box>

          {/* Botones */}
          <Button fullWidth variant='contained' onClick={doReceive}
            sx={{ borderRadius: '10px', py: 1, textTransform: 'none' }}>
            Recepcionar LPN con diferencias
          </Button>
          <Button fullWidth variant='outlined' onClick={() => setIncompleteModal(false)}
            sx={{ borderRadius: '10px', py: 0.8, textTransform: 'none' }}>
            Cancelar
          </Button>
        </Box>
      </Modal>

      {/* MODAL: Confirmar volver (se pierde el progreso) */}
      <Modal open={backConfirmModal} title='¿Deseas salir?' maxWidth='xs' onClose={() => setBackConfirmModal(false)}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 56, height: 56, borderRadius: '50%', backgroundColor: '#fff8e1', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <WarningAmberIcon sx={{ fontSize: 32, color: '#f59e0b' }} />
          </Box>
          <Typography variant='body1' textAlign='center'>
            Al volver se perderá el progreso de las LPNs escaneadas. ¿Está seguro/a que desea salir?
          </Typography>
          <Button fullWidth variant='contained' color='error'
            onClick={() => { setBackConfirmModal(false); prev() }}
            sx={{ borderRadius: '10px', py: 1, textTransform: 'none' }}>
            Sí, salir y perder el progreso
          </Button>
          <Button fullWidth variant='outlined' onClick={() => setBackConfirmModal(false)}
            sx={{ borderRadius: '10px', py: 0.8, textTransform: 'none' }}>
            No, permanecer aquí
          </Button>
        </Box>
      </Modal>
    </Box>
  )
}
